/**
 * AICopilotSession6Panel — Inbox extras for Session 6.
 *
 * Three collapsible blocks rendered above the message composer in the
 * conversation detail view:
 *   1. Abandoned Cart banner  — context + safe AI recovery reply (insertable)
 *   2. Recommended Products  — top 3 Shopify matches with "Send to chat"
 *   3. Suggested Follow-up Task — with "Create Task" CTA
 *
 * Each block self-hides when its server response is empty. Each block
 * is also independently gated by an `ai_copilot_settings` boolean so
 * tenants can enable them piecewise.
 *
 * NEVER auto-sends. Inserts proposed text into the composer for the
 * agent to review.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ShoppingCart, Sparkles, ClipboardCheck, ExternalLink, Loader2,
  AlertTriangle, ChevronDown, ChevronUp, Send,
} from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AICopilotSession6Panel({
  conversationId,
  customerPhone,
  customerEmail,
  onInsertSuggestion,
}) {
  const [abandoned, setAbandoned] = useState(null);
  const [products, setProducts] = useState([]);
  const [taskSuggestion, setTaskSuggestion] = useState(null);
  const [openAbandoned, setOpenAbandoned] = useState(true);
  const [openProducts, setOpenProducts] = useState(false);
  const [openTask, setOpenTask] = useState(true);
  const [creatingTask, setCreatingTask] = useState(false);
  const [taskCreated, setTaskCreated] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);
    try {
      const headers = authHeaders();
      const params = {
        conversation_id: conversationId,
        customer_phone: customerPhone,
        customer_email: customerEmail,
      };
      const [acRes, prodRes, taskRes] = await Promise.allSettled([
        axios.get(`${API_BASE}/ai-copilot/abandoned-checkout-context`, {
          headers,
          params: { ...params, conversation_id: conversationId },
        }),
        axios.post(`${API_BASE}/ai-copilot/product-recommendation`, params, { headers }),
        axios.post(`${API_BASE}/ai-copilot/suggest-follow-up-task`, params, { headers }),
      ]);

      if (acRes.status === 'fulfilled') {
        setAbandoned(acRes.value.data);
      } else {
        setAbandoned(null);
      }
      if (prodRes.status === 'fulfilled') {
        setProducts(prodRes.value.data?.products || []);
      } else {
        setProducts([]);
      }
      if (taskRes.status === 'fulfilled') {
        setTaskSuggestion(taskRes.value.data?.suggestion || null);
        setTaskCreated(false);
      } else {
        setTaskSuggestion(null);
      }
    } finally {
      setLoading(false);
    }
  }, [conversationId, customerPhone, customerEmail]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateTask = async () => {
    if (!taskSuggestion) return;
    setCreatingTask(true);
    try {
      const headers = authHeaders();
      const res = await axios.post(
        `${API_BASE}/ai-copilot/create-follow-up-task`,
        {
          ...taskSuggestion,
          conversation_id: conversationId,
          customer_phone: customerPhone,
          customer_email: customerEmail,
        },
        { headers },
      );
      setTaskCreated(true);
      if (res.data?.reason === 'duplicate_within_window') {
        // Silently treat as success — user already has one
      }
    } finally {
      setCreatingTask(false);
    }
  };

  const showAbandoned = abandoned && abandoned.abandoned;
  const showOrderPlaced = abandoned && abandoned.reason === 'order_placed_after';
  const showProducts = (products || []).length > 0;
  const showTask = taskSuggestion != null;

  if (!conversationId) return null;
  if (loading && !showAbandoned && !showProducts && !showTask) return null;
  if (!showAbandoned && !showOrderPlaced && !showProducts && !showTask) return null;

  return (
    <div className="space-y-2" data-testid="ai-copilot-session6-panel">
      {/* ───── Abandoned cart "already ordered" banner ───── */}
      {showOrderPlaced && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="abandoned-already-ordered">
          <span className="font-semibold">✓ Customer already placed an order</span>
          {abandoned.subsequent_order?.order_number && (
            <span className="ml-1 opacity-80">
              (#{abandoned.subsequent_order.order_number})
            </span>
          )}
        </div>
      )}

      {/* ───── Abandoned cart context ───── */}
      {showAbandoned && (
        <div className="rounded-lg border border-rose-300 bg-rose-50/70 overflow-hidden" data-testid="abandoned-checkout-block">
          <button
            type="button"
            onClick={() => setOpenAbandoned(v => !v)}
            className="w-full flex items-center justify-between px-3 py-2 hover:bg-rose-100/50"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-rose-900">
              <ShoppingCart className="w-4 h-4" /> Abandoned Cart
              {abandoned.checkout?.total_price != null && (
                <Badge className="bg-rose-200 text-rose-900 border-rose-300 text-[10px]">
                  {abandoned.checkout.currency || ''} {abandoned.checkout.total_price}
                </Badge>
              )}
            </span>
            {openAbandoned ? <ChevronUp className="w-4 h-4 text-rose-800" /> : <ChevronDown className="w-4 h-4 text-rose-800" />}
          </button>
          {openAbandoned && (
            <div className="px-3 pb-3 space-y-2 text-sm">
              {(abandoned.checkout?.line_items || []).slice(0, 3).map((li, idx) => (
                <div key={idx} className="flex items-center gap-2 text-rose-900">
                  {li.image_url && (
                    <img src={li.image_url} alt={li.title} className="w-8 h-8 rounded object-cover border border-rose-200" />
                  )}
                  <span className="flex-1 truncate">{li.title}</span>
                  <span className="text-xs">× {li.quantity}</span>
                  {li.price && <span className="text-xs font-semibold">{li.price}</span>}
                </div>
              ))}
              {abandoned.suggested_reply && (
                <div className="rounded-md bg-white border border-rose-200 p-2 text-rose-950">
                  <p className="text-xs uppercase tracking-wide text-rose-700 mb-1 font-semibold">Suggested recovery reply</p>
                  <p className="text-sm" data-testid="abandoned-suggested-reply">{abandoned.suggested_reply}</p>
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={() => onInsertSuggestion?.(abandoned.suggested_reply)}
                      data-testid="abandoned-insert-reply-btn"
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                    >
                      <Send className="w-3 h-3 mr-1" /> Insert into composer
                    </Button>
                    {abandoned.checkout?.abandoned_checkout_url && (
                      <a
                        href={abandoned.checkout.abandoned_checkout_url}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-rose-700 hover:underline"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" /> Open checkout
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ───── Product Recommendations ───── */}
      {showProducts && (
        <div className="rounded-lg border border-indigo-300 bg-indigo-50/70 overflow-hidden" data-testid="product-recommendation-block">
          <button
            type="button"
            onClick={() => setOpenProducts(v => !v)}
            className="w-full flex items-center justify-between px-3 py-2 hover:bg-indigo-100/50"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
              <Sparkles className="w-4 h-4" /> Recommended Products
              <Badge className="bg-indigo-200 text-indigo-900 border-indigo-300 text-[10px]">{products.length}</Badge>
            </span>
            {openProducts ? <ChevronUp className="w-4 h-4 text-indigo-800" /> : <ChevronDown className="w-4 h-4 text-indigo-800" />}
          </button>
          {openProducts && (
            <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {products.slice(0, 3).map((p) => (
                <div key={p.id} className="bg-white border border-indigo-200 rounded-md p-2 flex flex-col" data-testid={`product-card-${p.id}`}>
                  {p.image_url && (
                    <img src={p.image_url} alt={p.title} className="w-full h-24 object-cover rounded mb-2" />
                  )}
                  <p className="text-xs font-semibold text-indigo-900 line-clamp-2 flex-1">{p.title}</p>
                  {p.price && <p className="text-xs text-indigo-700 mt-1">{p.price}</p>}
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                    onClick={() => onInsertSuggestion?.(
                      `Have a look at ${p.title}${p.price ? ` — ${p.price}` : ''}${p.handle ? `: https://yourstore/products/${p.handle}` : ''}`
                    )}
                    data-testid={`product-insert-${p.id}`}
                  >
                    <Send className="w-3 h-3 mr-1" /> Send to chat
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ───── Suggested Follow-up Task ───── */}
      {showTask && (
        <div className="rounded-lg border border-amber-300 bg-amber-50/70 overflow-hidden" data-testid="follow-up-task-block">
          <button
            type="button"
            onClick={() => setOpenTask(v => !v)}
            className="w-full flex items-center justify-between px-3 py-2 hover:bg-amber-100/50"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <ClipboardCheck className="w-4 h-4" /> Suggested Follow-up Task
              <Badge className={`text-[10px] border ${
                taskSuggestion.priority === 'urgent' ? 'bg-red-200 text-red-900 border-red-300'
                : taskSuggestion.priority === 'high' ? 'bg-amber-200 text-amber-900 border-amber-300'
                : 'bg-slate-200 text-slate-900 border-slate-300'
              }`}>
                {taskSuggestion.priority}
              </Badge>
            </span>
            {openTask ? <ChevronUp className="w-4 h-4 text-amber-800" /> : <ChevronDown className="w-4 h-4 text-amber-800" />}
          </button>
          {openTask && (
            <div className="px-3 pb-3 space-y-2 text-sm">
              <p className="font-semibold text-amber-950">{taskSuggestion.title}</p>
              {taskSuggestion.ai_reason && (
                <p className="text-xs text-amber-800 italic">
                  <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1" />
                  {taskSuggestion.ai_reason}
                </p>
              )}
              {taskSuggestion.suggested_message && (
                <div className="rounded-md bg-white border border-amber-200 p-2">
                  <p className="text-xs uppercase tracking-wide text-amber-700 mb-1 font-semibold">Suggested message</p>
                  <p className="text-sm text-amber-950">{taskSuggestion.suggested_message}</p>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                {taskSuggestion.suggested_message && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onInsertSuggestion?.(taskSuggestion.suggested_message)}
                    data-testid="task-insert-suggestion-btn"
                    className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                  >
                    <Send className="w-3 h-3 mr-1" /> Insert reply
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleCreateTask}
                  disabled={creatingTask || taskCreated}
                  data-testid="create-followup-task-btn"
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                >
                  {creatingTask ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : taskCreated ? '✓ Task created'
                    : <><ClipboardCheck className="w-3 h-3 mr-1" /> Create Follow-up Task</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
